var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { appColors } from '@backrs_ui/common-ui/src/assets/appColors';
import { BackrsCard } from '@backrs_ui/common-ui/src/atoms/BackrsCard';
import { BadgeCircle } from '@backrs_ui/common-ui/src/atoms/BadgeCircle';
import { Box, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { NuggetArticle } from './NuggetArticle';
import { NuggetAudio } from './NuggetAudio';
import { NuggetDocument } from './NuggetDocument';
import { NuggetEvent } from './NuggetEvent';
import { NuggetInstitution } from './NuggetInstitution';
import { NuggetInternship } from './NuggetInternship/NuggetInternship';
import { NuggetOpportunity } from './NuggetOpportunity';
import { NuggetPost } from './NuggetPost';
import { NuggetProfile } from './NuggetProfile';
import { NuggetResource } from './NuggetResource';
import { NuggetScholarship } from './NuggetScholarship/NuggetScholarship';
import { NuggetTextResponse } from './NuggetTextResponse';
import { NuggetVideo } from './NuggetVideo';
import { PanelNuggetHeader } from './PanelNuggetHeader';
function variantMapper(variant, contentSize) {
    switch (variant.type) {
        case 'opportunity':
            return {
                component: _jsx(NuggetOpportunity, { variant: { type: 'SMALL', deadline: variant.deadline } }),
                icon: 'IconOpportunity',
            };
        case 'event':
            return {
                component: _jsx(NuggetEvent, { variant: { type: 'SMALL', date: variant.date } }),
                icon: 'IconEvent',
            };
        case 'video-external':
            return {
                component: (_jsx(NuggetVideo, { variant: {
                        type: 'SMALL',
                        url: variant.url,
                        previewImage: variant.previewImage,
                        isInternal: false,
                    } })),
                icon: 'IconVideo',
            };
        case 'video-internal':
            return {
                component: (_jsx(NuggetVideo, { variant: {
                        type: 'SMALL',
                        url: variant.url,
                        previewImage: variant.previewImage,
                        isInternal: true,
                    } })),
                icon: 'IconVideo',
            };
        case 'article':
            return {
                component: (_jsx(NuggetArticle, { variant: {
                        type: 'SMALL',
                        title: variant.title,
                        previewImage: variant.previewImage,
                    } })),
                icon: 'IconArticle',
            };
        case 'institution':
            return {
                component: (_jsx(NuggetInstitution, { variant: {
                        type: 'SMALL',
                        previewImage: variant.previewImage,
                    } })),
                icon: 'IconInstitution',
            };
        case 'audio':
            return {
                component: _jsx(NuggetAudio, { variant: { type: 'SMALL' } }),
                icon: 'IconAudio',
            };
        case 'document':
            return {
                component: (_jsx(NuggetDocument, { variant: {
                        type: 'SMALL',
                        title: variant.title,
                    } })),
                icon: 'IconDocument',
            };
        case 'resource':
            return {
                component: (_jsx(NuggetResource, { variant: { type: 'SMALL', previewImage: variant.previewImage, title: variant.title } })),
                icon: 'IconArticle',
            };
        case 'text-response':
            return {
                component: (_jsx(NuggetTextResponse, { variant: {
                        type: 'SMALL',
                        additionalQuote: variant.additionalQuote,
                        additionalQuoteAuthor: variant.additionalQuoteAuthor,
                        contentSize: contentSize,
                    } })),
                icon: 'IconTextResponse',
            };
        case 'post':
            return {
                component: (_jsx(NuggetPost, { variant: {
                        type: 'SMALL',
                        additionalQuote: variant.additionalQuote,
                        additionalQuoteAuthor: variant.additionalQuoteAuthor,
                        contentSize: contentSize,
                    } })),
                icon: 'IconTextResponse',
            };
        case 'book':
            return {
                component: (_jsx(NuggetResource, { variant: { type: 'SMALL', title: variant.title, previewImage: variant.previewImage } })),
                icon: 'IconBook',
            };
        case 'competency':
            return {
                component: _jsx(_Fragment, {}),
                icon: 'ProsperLogo',
            };
        case 'scholarships':
            return {
                component: (_jsx(NuggetScholarship, { contentSize: contentSize, variant: {
                        type: 'SMALL',
                        awardMoneyRangeMin: variant.scholarshipInfo.award_money_range_min,
                        awardMoneyRangeMax: variant.scholarshipInfo.award_money_range_max,
                        awardType: variant.scholarshipInfo.award_type,
                        awardCategory: variant.scholarshipInfo.award_category,
                        awardDeadline: variant.scholarshipInfo.submission_deadline,
                    } })),
                icon: 'IconScholarship',
            };
        case 'internship':
            return {
                component: (_jsx(NuggetInternship, { variant: { type: 'SMALL', internshipInfo: variant.internshipInfo }, contentSize: contentSize })),
                icon: 'IconBriefcase',
            };
        case 'profile-external':
            return {
                component: (_jsx(NuggetProfile, { variant: {
                        type: 'SMALL',
                        abbreviatedName: variant.abbreviatedName,
                        location: variant.location,
                        jobTitle: variant.jobTitle,
                        employer: variant.employer,
                        school: variant.school,
                        profileImageUrl: variant.profileImageUrl,
                        profileType: 'EXTERNAL',
                        contentSize: contentSize,
                    } })),
                icon: 'IconProfileExternal',
            };
        case 'profile-internal':
            return {
                component: (_jsx(NuggetProfile, { variant: {
                        type: 'SMALL',
                        abbreviatedName: variant.abbreviatedName,
                        location: variant.location,
                        jobTitle: variant.jobTitle,
                        employer: variant.employer,
                        school: variant.school,
                        role: variant.role,
                        profileImageUrl: variant.profileImageUrl,
                        profileType: 'INTERNAL',
                        contentSize: contentSize,
                    } })),
                icon: 'IconProfileInternal',
            };
        case 'career-path':
            return {
                component: _jsx(_Fragment, {}),
                icon: 'IconBriefcase',
            };
        default:
            return {
                component: _jsx(_Fragment, {}),
                icon: 'ProsperLogo',
            };
    }
}
export var PanelNugget = function (_a) {
    var title = _a.title, subTitle = _a.subTitle, body = _a.body, variant = _a.variant, onClick = _a.onClick, subText = _a.subText, _b = _a.size, size = _b === void 0 ? 'LARGE' : _b;
    var _c = variantMapper(variant, size), component = _c.component, icon = _c.icon;
    return (_jsx(BackrsCard, { onClick: onClick, sx: size === 'SMALL' ? smallContentSizeContainerStyle : largeContentSizeContainerStyle, children: _jsxs(Box, { sx: mainContainerStyle, children: [_jsxs(Box, { sx: contentContainerStyle, children: [_jsx(PanelNuggetHeader, { iconName: icon, title: title, size: size, subTitle: subTitle }), _jsxs(Box, { sx: { flex: 1 }, children: [body && (_jsx(Typography, { variant: "body1", sx: bodyTextStyle, children: body.replace(/--/g, '—') })), component] })] }), subText && (_jsx(Box, { sx: subTextContainerStyle, children: _jsxs(Typography, { sx: subTextWrapperStyle, children: [_jsx(Box, { mt: 0.5, children: _jsx(BadgeCircle, {}) }), _jsx(ReactMarkdown, { components: {
                                    p: function (_a) {
                                        var props = __rest(_a, []);
                                        return _jsx("p", __assign({ style: { margin: '0px' } }, props));
                                    },
                                }, children: subText })] }) }))] }) }));
};
var contentContainerStyle = {
    justifyContent: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    paddingX: 2,
    paddingY: 2,
};
var mainContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    '@media (min-width: 1300px)': {
        flexDirection: 'row',
    },
};
var subTextContainerStyle = {
    display: 'flex',
    borderTop: "1px solid ".concat(appColors.neutral.gray4),
    flexDirection: 'column',
    width: '100%',
    '@media (min-width: 1300px)': {
        width: '50%',
        borderLeft: "1px solid ".concat(appColors.neutral.gray4),
        borderTop: 'none',
        margin: '0px 16px',
    },
    '@media (min-width: 1450px)': {
        width: '75%',
    },
    '@media (min-width: 1600px)': {
        width: '100%',
    },
};
var subTextWrapperStyle = {
    fontSize: '10pt',
    width: '100%',
    boxSizing: 'border-box',
    color: appColors.neutral.charcoal,
    fontStyle: 'italic',
    paddingY: 2,
    paddingX: 2,
    display: 'flex',
    flexDirection: 'row',
    gap: 1,
};
var smallContentSizeContainerStyle = {
    padding: 0,
    border: "1px solid ".concat(appColors.neutral.black),
};
var largeContentSizeContainerStyle = {
    padding: 1,
    border: "1px solid ".concat(appColors.neutral.black),
    height: '100%',
};
var bodyTextStyle = {
    fontSize: '11pt',
    lineHeight: '1.4',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 5,
    textOverflow: 'ellipsis',
};
